import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts"

import { formatBytes } from "@/lib/utils"

interface DiskUsageData {
  name: string
  value: number
  color?: string
}

interface DiskUsagePieProps {
  data: DiskUsageData[]
  usedBytes?: number
  totalBytes?: number
  className?: string
}

const COLORS = [
  "#3b82f6", // blue
  "#10b981", // green
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#06b6d4", // cyan
  "#84cc16", // lime
]

export function DiskUsagePie({
  data,
  usedBytes,
  totalBytes,
  className,
}: DiskUsagePieProps) {
  const chartData = data.map((item, index) => ({
    ...item,
    color: item.color || COLORS[index % COLORS.length],
  }))

  const usagePercent =
    totalBytes && usedBytes ? ((usedBytes / totalBytes) * 100).toFixed(0) : null

  return (
    <div className={className}>
      {usagePercent && (
        <div className="mb-4 text-center">
          <div className="text-3xl font-bold">{usagePercent}%</div>
          <div className="text-muted-foreground text-sm">
            {formatBytes(usedBytes || 0)} of {formatBytes(totalBytes || 0)} used
          </div>
        </div>
      )}
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={2}
            dataKey="value"
            label={({ name, value }) => `${name}: ${formatBytes(value)}`}
            labelLine={false}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value: number | undefined) => formatBytes(value)}
            contentStyle={{
              backgroundColor: "hsl(var(--background))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "8px",
            }}
          />
          <Legend align="left" verticalAlign="bottom"
              itemSorter="dataKey"
              layout="horizontal" />
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}

export default DiskUsagePie
